/**
 * N8ONE — Therapeutic Zen Imagery Bank (Seasonally Shifting)
 *
 * A curated set of hero photographs (soft mist, still water, stone
 * sanctuaries, serene silhouettes) grouped into per-surface, per-season
 * sub-banks. Two rhythms compound:
 *
 *   1. Season picks the *palette* — spring feels fresh, summer feels golden,
 *      autumn feels amber-quiet, winter feels candlelit-still.
 *   2. Day-of-year picks *today's* photograph from within that palette.
 *
 * So a Monday in February and a Monday in July open the app to a
 * genuinely different mood, and returning within the same season still
 * greets you with a fresh photograph each day.
 *
 * Consumers keep using `HERO.<key>` — a Proxy transparently returns
 * today's pick. No page code needs to change.
 */

const U = (id, w = 1800) =>
  `https://images.unsplash.com/${id}?w=${w}&q=80&auto=format&fit=crop`;

// ---- Master photograph library (all verified therapeutic / zen / natural) -
const IMG = {
  mistRidge:         U("photo-1470071459604-3b5ec3a7fe05"),
  mistyForest:       U("photo-1447752875215-b2761acb3c5d"),
  morningRidge:      U("photo-1470071459604-3b5ec3a7fe05"),
  stillWaterSunrise: U("photo-1439066615861-d1af74d74000"),
  waterReflection:   U("photo-1439066615861-d1af74d74000"),
  pondRipples:       U("photo-1520962880247-cfaf541c8724"),
  branchWater:       U("photo-1487958449943-2429e8be8625"),
  spaStonesPlant:    U("photo-1544161515-4ab6ce6db874"),
  spaCandles:        U("photo-1519823551278-64ac92734fb1"),
  warmCandles:       U("photo-1602934585418-f588bea4215c"),
  zenStones:         U("photo-1508739773434-c26b3d09e071"),
  mistLake:          U("photo-1502082553048-f009c37129b9"),
  greenMoss:         U("photo-1470252649378-9c29740c9fa8"),
  goldenMeadow:      U("photo-1500964757637-c85e8a162699"),
  forestLight:       U("photo-1447752875215-b2761acb3c5d"),
  sereneFace:        U("photo-1512290923902-8a9f81dc236c"),
  yogaSilhouette:    U("photo-1506126613408-eca07ce68773"),
  handsHerbs:        U("photo-1544367567-0f2fcb009e0b"),
  quietNotebook:     U("photo-1490730141103-6cac27aaab94"),
  mistSilhouette:    U("photo-1518241353330-0f7941c2d9b5"),
};

/**
 * Palette shorthand — reusable seasonal picks. Each is a small array of
 * images that lean into that season's mood. Not every image is unique
 * per season; many belong across seasons (e.g. warm candles read winter
 * *and* an autumn evening).
 */
const P = {
  freshDawn:      [IMG.mistRidge, IMG.greenMoss, IMG.goldenMeadow, IMG.pondRipples], // spring
  goldenLight:    [IMG.goldenMeadow, IMG.mistRidge, IMG.morningRidge, IMG.yogaSilhouette], // summer
  amberQuiet:     [IMG.mistLake, IMG.forestLight, IMG.mistyForest, IMG.branchWater], // autumn
  candleStillness:[IMG.warmCandles, IMG.spaCandles, IMG.stillWaterSunrise, IMG.mistSilhouette], // winter

  freshWater:     [IMG.pondRipples, IMG.mistRidge, IMG.greenMoss],
  summerWater:    [IMG.stillWaterSunrise, IMG.pondRipples, IMG.mistRidge],
  autumnWater:    [IMG.mistLake, IMG.waterReflection, IMG.branchWater],
  winterWater:    [IMG.stillWaterSunrise, IMG.waterReflection, IMG.mistLake],

  freshBotanical: [IMG.greenMoss, IMG.goldenMeadow, IMG.forestLight],
  summerBotanical:[IMG.goldenMeadow, IMG.greenMoss, IMG.mistRidge],
  autumnBotanical:[IMG.forestLight, IMG.mistLake, IMG.mistyForest],
  winterBotanical:[IMG.warmCandles, IMG.spaCandles, IMG.spaStonesPlant],

  freshSanctuary: [IMG.spaStonesPlant, IMG.greenMoss, IMG.handsHerbs],
  summerSanctuary:[IMG.spaStonesPlant, IMG.zenStones, IMG.handsHerbs],
  autumnSanctuary:[IMG.spaCandles, IMG.warmCandles, IMG.zenStones],
  winterSanctuary:[IMG.warmCandles, IMG.spaCandles, IMG.spaStonesPlant],
};

// ---- Per-surface, per-season banks ---------------------------------------
// Every entry is { spring, summer, autumn, winter }.
const BANKS = {
  // Main tabs
  home:     { spring: P.freshDawn,       summer: P.goldenLight,    autumn: P.amberQuiet,     winter: P.candleStillness },
  becoming: { spring: P.freshBotanical,  summer: P.summerBotanical,autumn: P.autumnBotanical,winter: P.winterBotanical },
  rituals:  { spring: P.freshSanctuary,  summer: P.summerSanctuary,autumn: P.autumnSanctuary,winter: P.winterSanctuary },
  community:{ spring: [IMG.handsHerbs, IMG.greenMoss, IMG.goldenMeadow],
              summer: [IMG.handsHerbs, IMG.goldenMeadow, IMG.mistRidge],
              autumn: [IMG.handsHerbs, IMG.forestLight, IMG.mistLake],
              winter: [IMG.handsHerbs, IMG.warmCandles, IMG.spaCandles] },
  journal:  { spring: [IMG.quietNotebook, IMG.pondRipples, IMG.greenMoss],
              summer: [IMG.quietNotebook, IMG.stillWaterSunrise, IMG.goldenMeadow],
              autumn: [IMG.quietNotebook, IMG.warmCandles, IMG.forestLight],
              winter: [IMG.quietNotebook, IMG.warmCandles, IMG.spaCandles] },
  mirror:   { spring: P.freshWater,      summer: P.summerWater,    autumn: P.autumnWater,    winter: P.winterWater },
  tracking: { spring: [IMG.pondRipples, IMG.greenMoss, IMG.goldenMeadow],
              summer: [IMG.mistLake, IMG.goldenMeadow, IMG.mistRidge],
              autumn: [IMG.mistLake, IMG.forestLight, IMG.branchWater],
              winter: [IMG.stillWaterSunrise, IMG.warmCandles, IMG.mistSilhouette] },
  profile:  { spring: [IMG.yogaSilhouette, IMG.goldenMeadow, IMG.greenMoss],
              summer: [IMG.yogaSilhouette, IMG.goldenMeadow, IMG.mistRidge],
              autumn: [IMG.mistSilhouette, IMG.forestLight, IMG.mistLake],
              winter: [IMG.mistSilhouette, IMG.warmCandles, IMG.spaCandles] },

  // Rituals sub-tabs
  body:     { spring: [IMG.spaStonesPlant, IMG.greenMoss, IMG.handsHerbs],
              summer: [IMG.spaStonesPlant, IMG.zenStones, IMG.goldenMeadow],
              autumn: [IMG.warmCandles, IMG.spaCandles, IMG.zenStones],
              winter: [IMG.warmCandles, IMG.spaCandles, IMG.spaStonesPlant] },
  face:     { spring: [IMG.sereneFace, IMG.spaStonesPlant, IMG.handsHerbs],
              summer: [IMG.sereneFace, IMG.spaStonesPlant, IMG.goldenMeadow],
              autumn: [IMG.sereneFace, IMG.warmCandles, IMG.spaCandles],
              winter: [IMG.sereneFace, IMG.warmCandles, IMG.spaCandles] },

  // Soul universe
  soulLanding:      { spring: [IMG.greenMoss, IMG.goldenMeadow, IMG.forestLight],
                      summer: [IMG.goldenMeadow, IMG.mistRidge, IMG.greenMoss],
                      autumn: [IMG.forestLight, IMG.mistLake, IMG.mistyForest],
                      winter: [IMG.mistLake, IMG.warmCandles, IMG.stillWaterSunrise] },
  soulReflection:   { spring: P.freshWater, summer: P.summerWater, autumn: P.autumnWater, winter: P.winterWater },
  regulation:       { spring: [IMG.zenStones, IMG.pondRipples, IMG.greenMoss],
                      summer: [IMG.zenStones, IMG.branchWater, IMG.stillWaterSunrise],
                      autumn: [IMG.zenStones, IMG.mistLake, IMG.branchWater],
                      winter: [IMG.zenStones, IMG.warmCandles, IMG.stillWaterSunrise] },
  innerExploration: { spring: P.freshBotanical, summer: P.summerBotanical, autumn: P.autumnBotanical, winter: P.winterBotanical },
  breathIndex:      { spring: [IMG.morningRidge, IMG.pondRipples, IMG.greenMoss],
                      summer: [IMG.morningRidge, IMG.mistRidge, IMG.goldenMeadow],
                      autumn: [IMG.mistRidge, IMG.forestLight, IMG.waterReflection],
                      winter: [IMG.stillWaterSunrise, IMG.warmCandles, IMG.mistSilhouette] },
  nervousSystem:    { spring: [IMG.greenMoss, IMG.forestLight, IMG.pondRipples],
                      summer: [IMG.greenMoss, IMG.goldenMeadow, IMG.mistRidge],
                      autumn: [IMG.forestLight, IMG.mistyForest, IMG.mistLake],
                      winter: [IMG.mistSilhouette, IMG.warmCandles, IMG.stillWaterSunrise] },
};

// Daily Community-page nature rotator (unchanged consumer shape) —
// this array is the *master* rotator and stays evergreen; individual
// surface heroes carry the seasonal work.
const COVERS = [
  { url: U("photo-1470071459604-3b5ec3a7fe05", 1400), alt: "Misty mountain ridges at first light",   intention: "Notice one small victory." },
  { url: U("photo-1502082553048-f009c37129b9", 1400), alt: "Mist over a still mountain lake",         intention: "Move slowly with intention." },
  { url: U("photo-1439066615861-d1af74d74000", 1400), alt: "Still water reflecting warm dawn light", intention: "Rest is a practice, not a reward." },
  { url: U("photo-1447752875215-b2761acb3c5d", 1400), alt: "Soft light through a quiet forest",      intention: "Let one small thing bloom today." },
  { url: U("photo-1470252649378-9c29740c9fa8", 1400), alt: "Emerald moss on a quiet forest floor",   intention: "Step outside for one long breath." },
  { url: U("photo-1500964757637-c85e8a162699", 1400), alt: "A quiet meadow at dusk",                 intention: "Return to your own rhythm." },
  { url: U("photo-1520962880247-cfaf541c8724", 1400), alt: "Ripples on a still pond",                intention: "Small consistency compounds." },
];

function dayOfYear(date = new Date()) {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start + (start.getTimezoneOffset() - date.getTimezoneOffset()) * 60 * 1000;
  return Math.floor(diff / 86400000);
}

/**
 * Northern-hemisphere meteorological seasons.
 *   Winter: Dec / Jan / Feb
 *   Spring: Mar / Apr / May
 *   Summer: Jun / Jul / Aug
 *   Autumn: Sep / Oct / Nov
 */
export function seasonOf(date = new Date()) {
  const m = date.getMonth(); // 0-11
  if (m === 11 || m <= 1) return "winter";
  if (m <= 4) return "spring";
  if (m <= 7) return "summer";
  return "autumn";
}

/** Return today's photograph for the named surface, from the current season's bank. */
export function dailyHero(key, now = new Date()) {
  const entry = BANKS[key];
  if (!entry) return "";
  const bank = entry[seasonOf(now)] || [];
  if (bank.length === 0) return "";
  return bank[dayOfYear(now) % bank.length];
}

/**
 * `HERO.<key>` transparently returns today's pick for that surface
 * from the *current season's* sub-bank. `HERO.covers` returns the full
 * Community-page rotator array (unchanged).
 */
export const HERO = new Proxy(
  {},
  {
    get(_target, key) {
      if (key === "covers") return COVERS;
      if (typeof key === "string" && BANKS[key]) return dailyHero(key);
      return undefined;
    },
    has(_target, key) {
      return key === "covers" || Object.prototype.hasOwnProperty.call(BANKS, key);
    },
  }
);

export default HERO;
